<template>
    <div class="flex items-center justify-between mt-2">
        <div
            v-if="!hideCallerInfo"
            class="flex flex-col"
            :class="{'w-full': hideCallInfo}"
        >
            <span
                v-if="!isMobilePhoneNumber && !call.callerphone"
                class="font-medium mb-1"
                :style="fontSize"
            >
                {{ call.callername }}
            </span>
            <span
                v-else
                class="text-gray-500"
                :style="fontSize"
            ><bdi>{{ call.callerphone }}</bdi></span>
        </div>
        <div
            v-if="!hideCallInfo"
            class="flex items-center justify-between direction-icon"
        >
            <component
                :is="directionMappings[call.direction]"
                class="mx-1"
            />
            <el-tooltip
                v-if="call.callstatus === 'Hold'"
                placement="top"
                :open-delay="300"
                :content="$t('status.hold')"
            >
                <icon-hold class="w-4 h-4" />
            </el-tooltip>
            <div
                v-else
                class="w-4 h-4"
            />
            <slot
                name="threshold"
                :status-threshold="threshold"
            >
                <IconThreshold
                    v-if="threshold.show"
                    v-bind="threshold.styles"
                    class="mx-1"
                />
            </slot>
            <span
                class="font-semibold call-time"
                :style="fontSize"
            >{{ timerDisplayTime }}</span>
        </div>
    </div>
</template>
<script>
import { Tooltip } from 'element-ui'
import { getInitialTime } from '@/util/timeUtils'
import timerMixin from '@/mixins/timerMixin'
import {
    setupSoundQueue,
    removeSoundsFromQueueByIdWithHistoryKeep
} from '@/composables/useSoundsStoreData'
import {
    activeLayoutThresholdConfig,
    getSoundFromActiveLayout,
    widgetTableContentFontSize
} from '@/composables/globalData/useLayouts'
import { DEFAULT_FONT_SIZE_PX, DEFAULT_LAYOUT_FONT_STYLE } from '@/enum/default-layout-config'
import { timeFormatter } from '@/helpers/timeFormatter'

export default {
    components: {
        [Tooltip.name]: Tooltip,
    },
    mixins: [
        timerMixin
    ],
    props: {
        call: {
            type: Object,
            default: () => ({}),
        },
        settings: {
            type: Object,
            default: () => ({}),
        },
        hideCallerInfo: {
            type: Boolean,
            default: false,
        },
        hideCallInfo: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            timerState: {
                seconds: getInitialTime(this.call.callStarted_UTC_CLIENT),
            },
            directionMappings: {
                Outgoing: 'IconDirectionOutgoing',
                Incoming: 'IconDirectionIncoming',
                Click2Call: 'IconDirectionOutgoing',
            },
            fontSize: DEFAULT_FONT_SIZE_PX,
            statusThreshold: '',
            threshold: {
                show: false,
                styles: {
                    color: ''
                }
            }
        }
    },
    computed: {
        thresholdConfig () {
            return activeLayoutThresholdConfig.value
        },
        dynamicFontSize () {
            return widgetTableContentFontSize.value
        },
        isMobilePhoneNumber () {
            const regRule = /^[0-9+]+$/gm
            return regRule.test(this.call.callerphone)
        }
    },
    watch: {
        'timerState.seconds': {
            deep: true,
            handler (val) {
                if (Number(val) === 0) {
                    removeSoundsFromQueueByIdWithHistoryKeep(`${this.call.ivrid}-${this.call.callername}-${this.call.callerphone}`)
                }
                const minThreshold = this.call.callstatus === 'Hold' ? Number(this.thresholdConfig.HoldTimeWarning) : Number(this.thresholdConfig.ActiveCallWarning)
                const maxThreshold = this.call.callstatus === 'Hold' ? Number(this.thresholdConfig.HoldTimeLimit) : Number(this.thresholdConfig.ActiveCallLimit)
                let statusThreshold = ''

                if (Number(val) > maxThreshold) {
                    statusThreshold = this.call.callstatus === 'Hold' ? 'infoHold' : 'info'
                } else if (Number(val) < maxThreshold && Number(val) >= minThreshold) {
                    statusThreshold = this.call.callstatus === 'Hold' ? 'warningHold' : 'warning'
                }

                if (statusThreshold !== this.statusThreshold) {
                    removeSoundsFromQueueByIdWithHistoryKeep(`${this.call.ivrid}-${this.call.callername}-${this.call.callerphone}`)
                    this.statusThreshold = statusThreshold
                }
            }
        },
        'statusThreshold' (newVal, oldVal) {
            if (newVal === oldVal) {
                return
            }
            if (!oldVal || newVal !== oldVal) {
                this.threshold = this.makeThresholdObj()
            }
        },
    },
    methods: {
        getTimerValue () {
            return getInitialTime(this.call.callStarted_UTC_CLIENT)
        },
        makeThresholdObj () {
            let thresholdColor = ''
            let sound = ''
            if (!this.statusThreshold) {
                return {
                    show: false,
                    styles: {
                        color: thresholdColor,
                    }
                }
            }

            let show = true
            const seconds = this.timerState.seconds

            const minThreshold = this.call.callstatus === 'Hold' ? Number(this.thresholdConfig.HoldTimeWarning) : Number(this.thresholdConfig.ActiveCallWarning)
            const maxThreshold = this.call.callstatus === 'Hold' ? Number(this.thresholdConfig.HoldTimeLimit) : Number(this.thresholdConfig.ActiveCallLimit)

            const statusesObj = {
                infoHold: {
                    sound: 'SoundHoldTimeLimit',
                    color: this.thresholdConfig.HoldTimeLimitColor,
                    property: 'sound hold time limit',
                    type: 'hold info',
                    notification_value: Number(this.thresholdConfig.HoldTimeLimit)
                },
                warningHold: {
                    sound: 'SoundHoldTimeWarning',
                    color: this.thresholdConfig.HoldTimeWarningColor,
                    property: 'sound hold time warning',
                    type: 'hold warning',
                    notification_value: Number(this.thresholdConfig.HoldTimeWarning)
                },
                info: {
                    sound: 'SoundActiveCallLimit',
                    color: this.thresholdConfig.ActiveCallLimitColor,
                    property: 'sound active call limit',
                    type: 'info',
                    notification_value: Number(this.thresholdConfig.ActiveCallLimit)
                },
                warning: {
                    sound: 'SoundActiveCallWarning',
                    color: this.thresholdConfig.ActiveCallWarningColor,
                    property: 'sound active call warning',
                    type: 'warning',
                    notification_value: Number(this.thresholdConfig.ActiveCallWarning)
                }

            }
            let type = ''
            let property = ''
            let  notification_value = ''
            if (minThreshold > seconds) {
                show = false
            } else if (seconds >= maxThreshold) {
                thresholdColor = statusesObj[this.statusThreshold].color
                sound = getSoundFromActiveLayout(statusesObj[this.statusThreshold].sound)
                type = statusesObj[this.statusThreshold].type
                notification_value = statusesObj[this.statusThreshold].notification_value
                property = statusesObj[this.statusThreshold].property
            } else if (seconds <= maxThreshold && seconds >= minThreshold) {
                sound = getSoundFromActiveLayout(statusesObj[this.statusThreshold].sound)
                thresholdColor = statusesObj[this.statusThreshold].color
                type = statusesObj[this.statusThreshold].type
                notification_value = statusesObj[this.statusThreshold].notification_value
                property = statusesObj[this.statusThreshold].property
            }

            if (this.statusThreshold && sound) {
                const notificationData = {
                    notification_value: timeFormatter(notification_value),
                    current_value: this.timerDisplayTime,
                    type,
                    property,
                    caller: !this.isMobilePhoneNumber && !this.call.callerphone ? this.call.callername : this.call.callerphone,
                    ivrid: this.call.ivrid

                }
                const soundItem = {
                    id: `${this.call.ivrid}-${this.call.callername}-${this.call.callerphone}`,
                    sound: sound,
                    played: false,
                    isPlaying: false,
                    type: this.statusThreshold,
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.callInfoCardHtml', { ...notificationData })
                }
                setupSoundQueue(soundItem)
            }

            return {
                show,
                styles: {
                    color: thresholdColor,
                }
            }
        }
    },
    mounted () {
        this.startTimer()
        if (this.$el.parentNode.hasAttribute('userid')) {
            this.fontSize = this.dynamicFontSize
        } else {
            this.fontSize = DEFAULT_LAYOUT_FONT_STYLE
        }
    },
    beforeDestroy () {
        removeSoundsFromQueueByIdWithHistoryKeep(`${this.call.ivrid}-${this.call.callername}-${this.call.callerphone}`)
    },
}
</script>
<style scoped lang="scss">
.call-time {
    min-width: 48px;
    line-height: 1;
    text-align: center;
    color: var(--mainTextColor-color);
}

.rtl .direction-icon {
    display: flex;
    flex-direction: row-reverse;
}
</style>
